import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { 
  Phone, ArrowRight, Clock, Briefcase, Shield, Users, Sprout, 
  GraduationCap, FileCheck, HeartPulse, HandHeart, Heart, 
  DollarSign, FileText, ChevronDown, ChevronUp, Globe, Award,
  Brain, Zap, Eye, MessageCircle, Mail, MapPin, CircleCheck,
  ArrowLeft, Menu, X, Facebook, Instagram, Linkedin
} from "lucide-react";
import { useState, useEffect } from "react";
import "@/App.css";
import useAdTracking from "./hooks/useAdTracking";
import { trackWhatsAppClick, trackPhoneClick } from "./utils/analytics";

// Contact Info
const CONTACT = {
  phone: "(19) 3542-5237",
  phoneLink: "tel:+551935425237",
  whatsapp: "https://wa.me/551935425237?text=Olá! Gostaria de agendar uma análise do meu caso previdenciário.",
  email: "mecatti@rmnadvogados.com",
  mainSite: "https://www.lmecatti.adv.br"
};

// Services Data
const SERVICES = [
  {
    id: "aposentadoria-tempo-contribuicao",
    path: "/aposentadoria-tempo-contribuicao",
    title: "Aposentadoria por Tempo de Contribuição",
    shortTitle: "Tempo de Contribuição",
    description: "Análise completa do tempo de serviço, incluindo períodos especiais, para garantir o melhor benefício possível.",
    icon: Clock,
  },
  {
    id: "aposentadoria-idade",
    path: "/aposentadoria-idade",
    title: "Aposentadoria por Idade",
    shortTitle: "Por Idade",
    description: "Orientação sobre requisitos, planejamento e requerimento para aposentadoria por idade urbana e rural.",
    icon: Briefcase,
  },
  {
    id: "aposentadoria-especial",
    path: "/aposentadoria-especial",
    title: "Aposentadoria Especial",
    shortTitle: "Especial",
    description: "Reconhecimento de atividades insalubres, perigosas ou penosas para aposentadoria com tempo reduzido.",
    icon: Shield,
  },
  {
    id: "aposentadoria-pessoa-deficiencia",
    path: "/aposentadoria-pessoa-deficiencia",
    title: "Aposentadoria da Pessoa com Deficiência",
    shortTitle: "Pessoa com Deficiência",
    description: "Direito a regras diferenciadas conforme a Lei Complementar 142/2013, com tempo e idade reduzidos.",
    icon: Users,
  },
  {
    id: "aposentadoria-rural",
    path: "/aposentadoria-rural",
    title: "Aposentadoria Rural",
    shortTitle: "Rural",
    description: "Reconhecimento da atividade rural para trabalhadores do campo, segurados especiais e boia-fria.",
    icon: Sprout,
  },
  {
    id: "aposentadoria-professor",
    path: "/aposentadoria-professor",
    title: "Aposentadoria do Professor",
    shortTitle: "Professor",
    description: "Regras diferenciadas para profissionais da educação com tempo de magistério comprovado.",
    icon: GraduationCap,
  },
  {
    id: "revisao-beneficios",
    path: "/revisao-beneficios",
    title: "Revisão de Benefícios",
    shortTitle: "Revisão",
    description: "Correção de erros no cálculo do INSS. Recuperação de valores pagos a menor nos últimos 5 anos.",
    icon: FileCheck,
  },
  {
    id: "auxilio-doenca",
    path: "/auxilio-doenca",
    title: "Auxílio-Doença e Auxílio-Acidentário",
    shortTitle: "Auxílio-Doença",
    description: "Defesa em processos de concessão, restabelecimento e prorrogação de benefícios por incapacidade.",
    icon: HeartPulse,
  },
  {
    id: "bpc-loas",
    path: "/bpc-loas",
    title: "BPC/LOAS",
    shortTitle: "BPC/LOAS",
    description: "Benefício de Prestação Continuada para idosos e pessoas com deficiência em situação de vulnerabilidade.",
    icon: HandHeart,
  },
  {
    id: "pensao-morte",
    path: "/pensao-morte",
    title: "Pensão por Morte",
    shortTitle: "Pensão por Morte",
    description: "Garantia do benefício para dependentes, incluindo cônjuges, filhos, companheiros e pais.",
    icon: Heart,
  },
  {
    id: "isencao-imposto-renda",
    path: "/isencao-imposto-renda",
    title: "Isenção de Imposto de Renda",
    shortTitle: "Isenção IR",
    description: "Direito à isenção de IR para aposentados e pensionistas com doenças graves, com restituição de 5 anos.",
    icon: DollarSign,
  },
  {
    id: "planejamento-previdenciario",
    path: "/planejamento-previdenciario",
    title: "Planejamento Previdenciário",
    shortTitle: "Planejamento",
    description: "Estudo personalizado para definir a melhor estratégia de aposentadoria, evitando prejuízos futuros.",
    icon: FileText,
  },
];

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isServicePage = location.pathname !== "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{ background: '#faf6f0', borderBottom: '3px solid #782828', boxShadow: '0 2px 16px rgba(0,0,0,0.18)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between">
          {isServicePage ? (
            <Link
              to="/"
              data-testid="back-to-home"
              className="flex items-center gap-2 transition-colors"
              style={{ color: '#782828' }}
            >
              <ArrowLeft size={18} />
              <span className="text-sm font-semibold">Voltar</span>
            </Link>
          ) : (
            <a
              href={CONTACT.mainSite}
              data-testid="back-to-main"
              className="flex items-center"
            >
              <img
                src="/images/logo-rmn-navbar.png"
                alt="Romanelli Mecatti &amp; Nunes Advogados"
                className="h-14 w-auto object-contain"
              />
            </a>
          )}

          <div className="hidden lg:flex items-center gap-1">
            {SERVICES.slice(0, 6).map((service) => (
              <Link
                key={service.id}
                to={service.path}
                data-testid={`nav-${service.id}`}
                className={`px-3 py-2 text-xs font-semibold rounded-md transition-colors ${
                  location.pathname === service.path
                    ? "bg-[#782828]/10"
                    : "hover:bg-[#782828]/08"
                }`}
                style={{
                  color: location.pathname === service.path ? '#782828' : '#4a2020',
                }}
              >
                {service.shortTitle}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-whatsapp"
              onClick={trackWhatsAppClick}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-md"
              style={{ background: '#25D366', color: '#ffffff' }}
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden p-2"
              style={{ color: '#782828' }}
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden mt-2 pb-4 pt-3 rounded-lg" style={{ borderTop: '1px solid #d4b4b4' }}>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {SERVICES.map((service) => (
                <Link
                  key={service.id}
                  to={service.path}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${service.id}`}
                  className={`text-xs font-semibold py-2 px-3 rounded-md transition-colors ${
                    location.pathname === service.path
                      ? "bg-[#782828]/10"
                      : "hover:bg-[#782828]/08"
                  }`}
                  style={{ color: location.pathname === service.path ? '#782828' : '#4a2020' }}
                >
                  {service.shortTitle}
                </Link>
              ))}
            </div>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackWhatsAppClick}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold text-sm"
              style={{ background: '#25D366', color: '#ffffff' }}
            >
              <MessageCircle size={16} />
              Falar no WhatsApp
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Service Card Component (for homepage)
const ServiceCard = ({ service }) => {
  const IconComponent = service.icon;
  return (
    <Link
      to={service.path}
      data-testid={`service-card-${service.id}`}
      className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-amber-500 transition-all duration-300"
    >
      <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
        <IconComponent className="text-amber-500" size={24} />
      </div>
      <h3 className="text-lg font-bold text-white mb-3">{service.title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
      <span className="inline-flex items-center gap-1 mt-4 text-amber-500 text-sm font-medium group-hover:gap-2 transition-all">
        Saiba mais <ArrowRight size={14} />
      </span>
    </Link>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      data-testid="faq-item"
      className="bg-white rounded-lg border-2 border-slate-200 px-6 hover:border-amber-500 transition-colors duration-300"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="faq-toggle"
        className="w-full py-5 flex items-center justify-between text-left"
      >
        <h3 className="text-lg font-bold text-slate-900 hover:text-amber-600 pr-4">{question}</h3>
        <span className="text-slate-400 flex-shrink-0">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>
      {isOpen && (
        <div className="pb-5 text-slate-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

// CTA Section Component
const CTASection = () => (
  <section id="contato" data-testid="cta-section" className="py-20 bg-slate-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <div className="inline-block mb-4 px-4 py-2 bg-amber-500/20 text-amber-500 rounded-full text-sm font-bold uppercase tracking-wide">
          Fale Conosco
        </div>
        <h2 className="text-4xl font-bold text-white mb-4">Solicite Sua Análise Agora</h2>
        <p className="text-lg text-slate-400">
          Não deixe seus direitos para depois. Entre em contato e descubra como podemos ajudar você a conquistar o benefício que merece.
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Informações de Contato</h3>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <Phone className="text-amber-500" size={24} />
              </div>
              <div>
                <div className="font-bold text-white mb-1">Telefone/WhatsApp</div>
                <a href={CONTACT.phoneLink} onClick={trackPhoneClick} className="text-slate-300 hover:text-amber-500 transition-colors text-lg">
                  {CONTACT.phone}
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <Mail className="text-amber-500" size={24} />
              </div>
              <div>
                <div className="font-bold text-white mb-1">E-mail</div>
                <a href={`mailto:${CONTACT.email}`} className="text-slate-300 hover:text-amber-500 transition-colors break-all">
                  {CONTACT.email}
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <MapPin className="text-amber-500" size={24} />
              </div>
              <div>
                <div className="font-bold text-white mb-1">Atendimento</div>
                <p className="text-slate-300">Atendimento em todo o território nacional - 100% online</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <h4 className="font-bold text-white mb-3 text-lg">Análise do Caso</h4>
            <ul className="space-y-2 text-slate-300 text-sm">
              {[
                "Atendimento com orientação especializada",
                "Avaliação completa do seu caso previdenciário",
                "Identificação de todos os direitos disponíveis",
                "Resposta em até 24 horas"
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <CircleCheck className="text-amber-500 mr-2 flex-shrink-0 mt-0.5" size={18} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              onClick={trackWhatsAppClick}
              className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-slate-900 font-bold rounded-lg hover:bg-amber-400 transition-all duration-300 shadow-lg"
            >
              Falar no WhatsApp
              <ArrowRight className="ml-2" size={20} />
            </a>
            <a
              href={CONTACT.phoneLink}
              data-testid="cta-phone"
              onClick={trackPhoneClick}
              className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-bold rounded-lg border-2 border-amber-500 hover:bg-amber-500/10 transition-all duration-300"
            >
              <Phone className="mr-2" size={20} />
              Ligar Agora
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Related Services Component
const RelatedServices = ({ currentServiceId }) => {
  const otherServices = SERVICES.filter(s => s.id !== currentServiceId).slice(0, 3);
  
  return (
    <section className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Outros Serviços</h2>
          <p className="text-slate-400">Conheça outras áreas de atuação do nosso escritório</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {otherServices.map(service => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-amber-500 font-medium hover:text-amber-400"
          >
            Ver todos os serviços <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => (
  <a
    href={CONTACT.whatsapp}
    className="whatsapp-float"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Falar no WhatsApp"
    data-testid="whatsapp-float"
    onClick={trackWhatsAppClick}
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
    </svg>
  </a>
);

// Process Section Component
// Google Reviews Section
const GoogleReviews = () => {
  const reviews = [
    {
      name: "Jaquelini Souza",
      date: "Há 9 semanas",
      initial: "J",
      color: "from-red-800 to-yellow-600",
      text: "É com imensa satisfação que compartilho minha experiência com o meu advogado, Dr. Lucas Mecatti. Sem dúvida alguma, ele é o melhor profissional da cidade! Sua eficiência é notável, e a qualidade do trabalho que ele entrega é impecável, sempre superando as expectativas. Além de ser um excelente profissional, é uma pessoa de uma bondade ímpar, extremamente honesta e íntegra em todas as suas ações. Recomendo de olhos fechados!"
    },
    {
      name: "Juliana Ramos",
      date: "29 de jul. de 2024",
      initial: "J",
      color: "from-blue-700 to-purple-600",
      text: "É incrível como o Dr. Lucas Mecatti consegue aplicar as leis de forma clara e precisa para obter resultados positivos. Sua habilidade em argumentar é admirável! Além de ser um advogado talentoso, é extremamente ético. Sua integridade e honestidade fazem de você um profissional exemplar. Sua dedicação ao meu caso superou todas as minhas expectativas. Muito obrigada pelo trabalho excepcional!"
    },
    {
      name: "Ely Ezer Furini Miguel",
      date: "28 de jun. de 2024",
      initial: "E",
      color: "from-emerald-600 to-teal-500",
      text: "Excelente profissional, super atencioso, dedicado em buscar uma resolução para o problema identificado com excelente custo e benefício! Recomendo com certeza."
    },
    {
      name: "Sebastião Rocha",
      date: "Há 20 semanas",
      initial: "S",
      color: "from-amber-700 to-yellow-500",
      text: "O Dr. Lucas Mecatti é um advogado excepcional. Demonstrou profundo conhecimento jurídico e dedicação ao meu caso. Sempre disponível para esclarecer dúvidas e orientar sobre cada etapa do processo. Profissional de altíssimo nível, ético e comprometido com os direitos dos seus clientes. Recomendo sem hesitar!"
    }
  ];

  const GoogleIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" className="inline-block">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );

  return (
    <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
            <GoogleIcon /> Avaliações Google
          </span>
          <h2 className="text-3xl font-extrabold text-white mb-2">
            O Que Nossos <span className="text-yellow-400">Clientes Dizem</span>
          </h2>
          <p className="text-slate-400 text-sm">Avaliações reais de clientes atendidos pelo Dr. Lucas Mecatti</p>
        </div>

        {/* Nota geral */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-8 py-4">
            <div className="text-center">
              <div className="text-4xl font-black text-white leading-none">5,0</div>
              <div className="text-yellow-400 text-xl tracking-widest mt-1">★★★★★</div>
            </div>
            <div className="border-l border-white/10 pl-6">
              <div className="text-white font-bold text-sm">Nota máxima</div>
              <div className="text-slate-400 text-xs mt-1">Baseado em avaliações verificadas</div>
            </div>
            <div className="border-l border-white/10 pl-6 flex items-center gap-2">
              <GoogleIcon />
              <span className="text-slate-400 text-sm font-bold">Google</span>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {reviews.map((review, i) => (
            <div key={i} className="bg-white/4 border border-white/8 rounded-2xl p-6 relative hover:border-yellow-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="absolute top-4 right-5 text-5xl leading-none text-yellow-500/20 font-serif select-none">"</div>
              <div className="text-yellow-400 text-sm tracking-wider mb-3">★★★★★</div>
              <p className="text-slate-300 text-sm leading-relaxed italic mb-5">"{review.text}"</p>
              <div className="flex items-center gap-3 border-t border-white/7 pt-4">
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${review.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                  {review.initial}
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm">{review.name}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{review.date}</div>
                </div>
                <div className="flex items-center gap-1 text-slate-500 text-xs font-bold">
                  <GoogleIcon /> Google
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="https://www.google.com/search?q=Lucas+Mecatti+Sociedade+Individual+de+Advocacia"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200"
          >
            <GoogleIcon /> Ver todas as avaliações no Google
          </a>
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => (
  <section id="como-funciona" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <div className="inline-block mb-4 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-bold uppercase tracking-wide">
          Como Funciona
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">Um Caminho Claro Para Seus Direitos</h2>
        <p className="text-lg text-slate-700">Processo transparente e eficiente, do início ao resultado</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { number: "01", title: "Análise do Caso", description: "Avaliamos sua situação previdenciária de forma completa, identificando todas as oportunidades e direitos disponíveis." },
          { number: "02", title: "Estratégia Jurídica Personalizada", description: "Desenvolvemos um plano de ação sob medida, considerando as peculiaridades do seu caso para maximizar os resultados." },
          { number: "03", title: "Atuação Administrativa ou Judicial", description: "Conduzimos seu processo com urgência e técnica, seja no INSS, na via administrativa ou perante a Justiça Federal." },
          { number: "04", title: "Acompanhamento Contínuo", description: "Você recebe atualizações em cada etapa do processo, com total transparência até a conclusão definitiva do seu caso." }
        ].map((step, i) => (
          <div key={i} className="relative text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 text-white rounded-full text-2xl font-bold mb-6 shadow-lg">
              {step.number}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
            <p className="text-slate-600 leading-relaxed">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// FAQ Home Section Component
const FAQHome = () => {
  const faqs = [
    { id: "faq-1", question: "Quais tipos de aposentadoria vocês trabalham?", answer: "Trabalhamos com todos os tipos de aposentadoria: por tempo de contribuição, por idade, especial, rural, do professor, da pessoa com deficiência, além de auxílios, pensões e revisões de benefícios já concedidos." },
    { id: "faq-2", question: "Minha aposentadoria pode estar com valor errado?", answer: "Sim, é comum haver erros de cálculo pelo INSS. Realizamos revisões que podem aumentar significativamente o valor do benefício e ainda garantir o recebimento retroativo dos últimos 5 anos." },
    { id: "faq-3", question: "Sou aposentado e tenho doença grave. Posso ter isenção de IR?", answer: "Sim! Aposentados e pensionistas com doenças graves têm direito à isenção total de Imposto de Renda, além da restituição dos valores pagos nos últimos 5 anos." },
    { id: "faq-4", question: "O que é o BPC/LOAS e quem tem direito?", answer: "O BPC/LOAS é um benefício assistencial de um salário mínimo mensal para idosos acima de 65 anos ou pessoas com deficiência de qualquer idade que comprovem baixa renda familiar." },
    { id: "faq-5", question: "Vocês atendem apenas pela internet?", answer: "Nosso atendimento é 100% online, permitindo auxiliar clientes em todo o Brasil com a mesma qualidade e eficiência, através de videochamadas, WhatsApp e e-mail. Atendemos também presencialmente em nossos escritórios em CAMPINAS/SP: Av. Barão de Itapura, 1026 - Vila Itapura e ARARAS/SP: R. José Bonifácio, 139 - Centro." },
    { id: "faq-6", question: "Quanto tempo leva um processo previdenciário?", answer: "O prazo varia conforme a complexidade. Na via administrativa, pode levar de 45 a 90 dias. Na via judicial, de 6 meses a 2 anos, mas é possível obter liminares urgentes em casos específicos." },
    { id: "faq-7", question: "Já me aposentei há anos. Ainda posso pedir revisão?", answer: "Sim! É possível revisar benefícios concedidos há até 10 anos. A revisão pode corrigir erros e garantir o recebimento retroativo dos últimos 5 anos com o valor corrigido." },
    { id: "faq-8", question: "Quais documentos preciso para iniciar uma consulta?", answer: "Inicialmente, apenas RG, CPF e CNIS (Cadastro Nacional de Informações Sociais). Durante a consulta inicial, orientaremos sobre outros documentos necessários para seu caso específico." }
  ];
  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-bold uppercase tracking-wide">
            Tire Suas Dúvidas
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Perguntas Frequentes</h2>
          <p className="text-lg text-slate-700">Respostas claras sobre direito previdenciário</p>
        </div>
        <div className="space-y-4">
          {faqs.map((faq) => (
            <FAQItem key={faq.id} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Site Footer Component
const SiteFooter = () => {
  const currentYear = new Date().getFullYear();
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Sobre */}
          <div>
            <a href="/" className="inline-block mb-4">
              <img
                src="/images/logo-rmn-footer.png"
                alt="Romanelli Mecatti &amp; Nunes Advogados"
                className="h-14 w-auto object-contain"
              />
            </a>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Advocacia especializada em Direito Previdenciário com mais de 20 anos de experiência defendendo seus direitos.
            </p>
            <div className="flex space-x-3">
              <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/drlucasmecatti" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors">
                <Instagram size={20} />
              </a>
              <a href="https://www.linkedin.com/in/lucas-mecatti" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Linkedin size={20} />
              </a>
            </div>
          </div>
          {/* Links Rápidos */}
          <div>
            <h3 className="text-lg font-bold mb-4">Links Rápidos</h3>
            <ul className="space-y-2 text-sm">
              {['sobre', 'servicos', 'como-funciona', 'faq', 'contato'].map((item) => (
                <li key={item}>
                  <button onClick={() => scrollToSection(item)} className="text-slate-400 hover:text-white transition-colors">
                    {item === 'como-funciona' ? 'Como Funciona' : item.charAt(0).toUpperCase() + item.slice(1)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {/* Serviços */}
          <div>
            <h3 className="text-lg font-bold mb-4">Principais Serviços</h3>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>Aposentadorias</li>
              <li>Revisão de Benefícios</li>
              <li>Auxílio-Doença</li>
              <li>BPC/LOAS</li>
              <li>Pensão por Morte</li>
              <li>Isenção de IR</li>
            </ul>
          </div>
          {/* Contato */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contato</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start space-x-3">
                <Phone size={18} className="text-amber-400 mt-0.5" />
                <a href={CONTACT.phoneLink} onClick={trackPhoneClick} className="text-slate-400 hover:text-white">{CONTACT.phone}</a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-amber-400 mt-0.5" />
                <a href={`mailto:${CONTACT.email}`} className="text-slate-400 hover:text-white break-all">{CONTACT.email}</a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-amber-400 mt-0.5" />
                <span className="text-slate-400">Atendimento em todo o território nacional - 100% online</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-amber-400 mt-0.5" />
                <span className="text-slate-400"><strong className="text-slate-300">CAMPINAS/SP:</strong> Av. Barão de Itapura, 1026 - Vila Itapura</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-amber-400 mt-0.5" />
                <span className="text-slate-400"><strong className="text-slate-300">ARARAS/SP:</strong> R. José Bonifácio, 139 - Centro</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8">
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-400 text-center">
              <strong className="text-slate-300">Aviso Legal:</strong> Atendimento jurídico de natureza informativa, nos termos do Provimento 205/2021 da OAB.
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">© {currentYear} Romanelli Mecatti &amp; Nunes Advogados — Todos os direitos reservados</p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-slate-400 hover:text-white">Política de Privacidade</a>
              <a href="#" className="text-slate-400 hover:text-white">Termos de Uso</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Page Layout Component
const PageLayout = ({ children }) => (
  <div className="min-h-screen bg-slate-900">
    <Navbar />
    <main>{children}</main>
    <WhatsAppFloat />
    <SiteFooter />
  </div>
);

// Service Page Template
const ServicePage = ({ service, content }) => {
  const IconComponent = service.icon;
  
  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 bg-slate-800/50 border border-amber-500/30 rounded-full text-sm font-medium text-amber-500">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span>Direito Previdenciário</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
              {service.title}
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animate-delay-100">
              {content.heroDescription}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-200">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                onClick={trackWhatsAppClick}
                className="group inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-slate-900 font-bold rounded-lg hover:bg-amber-400 transition-all duration-300 shadow-lg hover:shadow-amber-500/50"
              >
                Solicite sua Análise
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </a>
              <a
                href={CONTACT.phoneLink}
                data-testid="hero-phone-btn"
                onClick={trackPhoneClick}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-bold rounded-lg border-2 border-white hover:bg-white/10 transition-all duration-300"
              >
                <Phone className="mr-2" size={20} />
                {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-bold uppercase tracking-wide">
                Sobre
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">{content.aboutTitle}</h2>
              <h3 className="text-2xl font-semibold text-amber-600 mb-6">{content.aboutSubtitle}</h3>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                {content.aboutParagraphs.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {content.highlights.map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <item.icon className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-500/20 text-amber-500 rounded-full text-sm font-bold uppercase tracking-wide">
              Como Atuamos
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">{content.featuresTitle}</h2>
            <p className="text-lg text-slate-400">{content.featuresSubtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.features.map((feature, i) => (
              <div key={i} className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-amber-500 transition-all duration-300">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                  <feature.icon className="text-amber-500" size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-bold uppercase tracking-wide">
              Tire Suas Dúvidas
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Perguntas Frequentes</h2>
            <p className="text-lg text-slate-700">Respostas claras sobre {service.title.toLowerCase()}</p>
          </div>
          <div className="space-y-4">
            {content.faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
      <RelatedServices currentServiceId={service.id} />
    </PageLayout>
  );
};

// ========== PAGE CONTENT DATA ==========

const pageContents = {
  "aposentadoria-tempo-contribuicao": {
    heroDescription: "Análise completa do tempo de serviço, incluindo períodos especiais, vínculos não reconhecidos e todo histórico contributivo para garantir o melhor benefício possível.",
    aboutTitle: "Aposentadoria por Tempo de Contribuição",
    aboutSubtitle: "Maximize seu benefício com análise especializada",
    aboutParagraphs: [
      "A aposentadoria por tempo de contribuição passou por mudanças significativas com a <strong>Reforma da Previdência de 2019</strong>. Hoje existem diversas regras de transição que podem beneficiar quem já estava contribuindo antes das mudanças.",
      "Nossa análise identifica <strong>todos os períodos contributivos</strong>, incluindo vínculos não reconhecidos, tempo especial, tempo rural e outros que podem acelerar sua aposentadoria ou aumentar o valor do benefício."
    ],
    highlights: [
      { icon: Clock, title: "Análise de Tempo", description: "Verificação completa do CNIS" },
      { icon: FileCheck, title: "Regras de Transição", description: "Identificação da melhor regra" },
      { icon: Shield, title: "Tempo Especial", description: "Conversão e reconhecimento" },
      { icon: Award, title: "Melhor Benefício", description: "Estratégia personalizada" }
    ],
    featuresTitle: "Serviços Especializados",
    featuresSubtitle: "Atuação completa para garantir seus direitos",
    features: [
      { icon: Clock, title: "Análise do CNIS", description: "Verificação detalhada de todo histórico contributivo, identificando pendências e vínculos não reconhecidos." },
      { icon: FileCheck, title: "Regras de Transição", description: "Análise das 5 regras de transição para identificar qual é mais vantajosa no seu caso específico." },
      { icon: Shield, title: "Tempo Especial", description: "Reconhecimento de períodos em atividade especial que podem ser convertidos ou reduzir o tempo necessário." },
      { icon: Sprout, title: "Tempo Rural", description: "Comprovação de trabalho rural para contagem de tempo, mesmo sem contribuição ao INSS." },
      { icon: Brain, title: "Simulação de Cenários", description: "Comparativo entre diferentes datas de aposentadoria para identificar o momento ideal." },
      { icon: FileText, title: "Requerimento Completo", description: "Preparação e acompanhamento de todo processo junto ao INSS ou Justiça Federal." }
    ],
    faqs: [
      { question: "Ainda existe aposentadoria por tempo de contribuição?", answer: "Sim, mas apenas para quem já estava contribuindo antes de 13/11/2019. Existem 5 regras de transição: por pontos, idade progressiva, pedágio de 50%, pedágio de 100% e a regra do direito adquirido. Cada pessoa pode se enquadrar em regras diferentes." },
      { question: "Quanto tempo preciso para aposentar?", answer: "Depende da regra aplicável. Na regra de pontos, por exemplo, é necessário 35 anos (homem) ou 30 anos (mulher) de contribuição, mais atingir a pontuação mínima (soma da idade + tempo de contribuição). Em 2024, são 105 pontos para homens e 95 para mulheres." },
      { question: "Posso incluir tempo de trabalho sem registro?", answer: "Sim, períodos sem registro podem ser comprovados através de documentos como contratos, recibos, declarações de empregadores e testemunhas. Também é possível reconhecer tempo rural anterior a 1991 e tempo especial não reconhecido." },
      { question: "Como saber qual regra de transição é melhor para mim?", answer: "É necessário analisar seu histórico completo de contribuições, idade e tempo de serviço. Cada regra tem requisitos e formas de cálculo diferentes. Nosso escritório faz essa análise técnica para identificar a opção mais vantajosa." }
    ]
  },
  
  "aposentadoria-idade": {
    heroDescription: "Orientação completa sobre requisitos, planejamento e requerimento para aposentadoria por idade urbana e rural, garantindo o melhor momento para solicitar seu benefício.",
    aboutTitle: "Aposentadoria por Idade",
    aboutSubtitle: "Planeje sua aposentadoria com segurança",
    aboutParagraphs: [
      "A aposentadoria por idade é uma das modalidades mais comuns de benefício previdenciário. Com a <strong>Reforma da Previdência</strong>, as regras foram alteradas, mas existem possibilidades de transição para quem já estava no sistema.",
      "Nosso trabalho é garantir que você <strong>não perca nenhum direito</strong> e receba o melhor benefício possível, considerando todo seu histórico contributivo e a melhor regra aplicável ao seu caso."
    ],
    highlights: [
      { icon: Clock, title: "Idade Mínima", description: "65 anos (H) ou 62 anos (M)" },
      { icon: FileCheck, title: "Carência", description: "180 contribuições mensais" },
      { icon: Sprout, title: "Rural", description: "Idade reduzida em 5 anos" },
      { icon: Award, title: "Melhor Cálculo", description: "Análise do valor ideal" }
    ],
    featuresTitle: "Como Podemos Ajudar",
    featuresSubtitle: "Serviços especializados em aposentadoria por idade",
    features: [
      { icon: Clock, title: "Verificação de Requisitos", description: "Análise se você já cumpre idade e carência necessárias para aposentar." },
      { icon: FileCheck, title: "Cálculo do Benefício", description: "Simulação do valor da aposentadoria considerando sua média de contribuições." },
      { icon: Sprout, title: "Aposentadoria Rural", description: "Orientação específica para trabalhadores rurais com idade reduzida (60H/55M)." },
      { icon: Brain, title: "Planejamento", description: "Identificação do melhor momento para solicitar o benefício." },
      { icon: FileText, title: "Requerimento", description: "Preparação completa da documentação e acompanhamento do processo." },
      { icon: Shield, title: "Recurso", description: "Atuação em casos de indeferimento ou cálculo incorreto do INSS." }
    ],
    faqs: [
      { question: "Qual a idade mínima para aposentar por idade?", answer: "Atualmente, a idade mínima é 65 anos para homens e 62 anos para mulheres na aposentadoria urbana. Para trabalhadores rurais, a idade é reduzida em 5 anos: 60 anos (homens) e 55 anos (mulheres)." },
      { question: "Quantas contribuições preciso ter?", answer: "São necessárias 180 contribuições mensais (15 anos) para a carência. Para homens que começaram a contribuir após a Reforma, o tempo mínimo de contribuição passou para 20 anos." },
      { question: "Como é calculado o valor da aposentadoria?", answer: "O cálculo considera 60% da média de todos os salários de contribuição desde julho/94, mais 2% para cada ano que exceder 15 anos de contribuição (mulheres) ou 20 anos (homens)." },
      { question: "Posso aposentar por idade mesmo contribuindo como autônomo?", answer: "Sim, contribuições como autônomo (contribuinte individual) contam normalmente para aposentadoria por idade. O importante é atingir a idade mínima e a carência de 180 contribuições." }
    ]
  },

  "aposentadoria-especial": {
    heroDescription: "Reconhecimento de atividades insalubres, perigosas ou penosas para aposentadoria com tempo reduzido de 15, 20 ou 25 anos, garantindo seus direitos como trabalhador exposto a riscos.",
    aboutTitle: "Aposentadoria Especial",
    aboutSubtitle: "Direito de quem trabalha exposto a riscos",
    aboutParagraphs: [
      "A aposentadoria especial é destinada a trabalhadores que exercem atividades com <strong>exposição a agentes nocivos</strong> à saúde, como ruído, calor, substâncias químicas, eletricidade, entre outros.",
      "Com nossa experiência, identificamos <strong>todos os períodos especiais</strong> da sua vida laboral, garantindo o reconhecimento correto para uma aposentadoria mais vantajosa e em tempo reduzido."
    ],
    highlights: [
      { icon: Shield, title: "15, 20 ou 25 Anos", description: "Tempo reduzido de contribuição" },
      { icon: FileCheck, title: "PPP e LTCAT", description: "Análise de documentação técnica" },
      { icon: Clock, title: "Conversão", description: "Tempo especial em comum" },
      { icon: Award, title: "Direito Adquirido", description: "Regras anteriores à Reforma" }
    ],
    featuresTitle: "Especialidades",
    featuresSubtitle: "Atuação completa em aposentadoria especial",
    features: [
      { icon: Shield, title: "Reconhecimento de Atividade", description: "Identificação e comprovação de períodos em atividade especial não reconhecidos pelo INSS." },
      { icon: FileCheck, title: "Análise de PPP", description: "Verificação do Perfil Profissiográfico Previdenciário e orientação para correções." },
      { icon: FileText, title: "Obtenção de LTCAT", description: "Auxílio na obtenção do Laudo Técnico das Condições Ambientais de Trabalho." },
      { icon: Clock, title: "Conversão de Tempo", description: "Transformação do período especial em tempo comum com multiplicadores favoráveis." },
      { icon: Brain, title: "Enquadramento por Categoria", description: "Reconhecimento de atividades especiais por profissão até 28/04/1995." },
      { icon: Zap, title: "Ação Judicial", description: "Processos para reconhecimento de tempo especial negado administrativamente." }
    ],
    faqs: [
      { question: "Quais profissões têm direito à aposentadoria especial?", answer: "Não existe lista fechada de profissões. O direito depende da comprovação da exposição a agentes nocivos. Exemplos comuns: médicos, enfermeiros, dentistas, eletricistas, soldadores, metalúrgicos, motoristas de caminhão, vigilantes, trabalhadores em frigoríficos, mineradores, entre outros." },
      { question: "O que mudou com a Reforma da Previdência?", answer: "Após 13/11/2019, além do tempo de exposição (15, 20 ou 25 anos), passou a ser exigida idade mínima (55, 58 ou 60 anos, respectivamente). Para quem já tinha direito antes da Reforma, as regras anteriores são mantidas." },
      { question: "Posso converter tempo especial em tempo comum?", answer: "Sim, o tempo especial pode ser convertido em tempo comum usando multiplicadores (1,4 para homens e 1,2 para mulheres). Isso é útil quando não há tempo suficiente para aposentadoria especial, mas a conversão pode acelerar outra modalidade." },
      { question: "O que é PPP e LTCAT?", answer: "PPP (Perfil Profissiográfico Previdenciário) é documento que comprova a exposição a agentes nocivos. LTCAT (Laudo Técnico das Condições Ambientais de Trabalho) é o laudo técnico que embasa o PPP. Ambos são fundamentais para comprovar atividade especial." }
    ]
  },

  "aposentadoria-pessoa-deficiencia": {
    heroDescription: "Direito a regras diferenciadas conforme a Lei Complementar 142/2013, com tempo de contribuição e idade reduzidos de acordo com o grau de deficiência.",
    aboutTitle: "Aposentadoria da Pessoa com Deficiência",
    aboutSubtitle: "Regras especiais garantidas por lei",
    aboutParagraphs: [
      "A pessoa com deficiência tem direito a regras <strong>diferenciadas</strong> para aposentadoria, com redução do tempo de contribuição ou da idade mínima, conforme estabelecido na <strong>Lei Complementar 142/2013</strong>.",
      "O grau da deficiência (leve, moderada ou grave) é avaliado por perícia médica e social do INSS, determinando o tempo necessário para aposentadoria."
    ],
    highlights: [
      { icon: Users, title: "Tempo Reduzido", description: "25 a 33 anos (conforme grau)" },
      { icon: Clock, title: "Idade Reduzida", description: "55 anos (H) ou 50 anos (M)" },
      { icon: Shield, title: "Proteção Legal", description: "LC 142/2013" },
      { icon: Award, title: "Perícia Especial", description: "Avaliação biopsicossocial" }
    ],
    featuresTitle: "Nossos Serviços",
    featuresSubtitle: "Atuação especializada em direitos da pessoa com deficiência",
    features: [
      { icon: Users, title: "Orientação Completa", description: "Explicação detalhada sobre requisitos e direitos da pessoa com deficiência." },
      { icon: FileCheck, title: "Preparação para Perícia", description: "Orientação sobre documentação médica necessária para avaliação do grau de deficiência." },
      { icon: Shield, title: "Acompanhamento de Perícia", description: "Suporte durante todo processo de avaliação biopsicossocial do INSS." },
      { icon: Brain, title: "Recurso Administrativo", description: "Atuação em casos de classificação incorreta do grau de deficiência." },
      { icon: FileText, title: "Ação Judicial", description: "Processos para reconhecimento do direito negado pelo INSS." },
      { icon: Clock, title: "Revisão de Benefício", description: "Análise de aposentadorias já concedidas para verificar possibilidade de melhoria." }
    ],
    faqs: [
      { question: "Quanto tempo preciso para aposentar como pessoa com deficiência?", answer: "Depende do grau da deficiência: Grave - 25 anos (H) ou 20 anos (M); Moderada - 29 anos (H) ou 24 anos (M); Leve - 33 anos (H) ou 28 anos (M). Também existe aposentadoria por idade aos 60 anos (H) ou 55 anos (M) com 15 anos de contribuição." },
      { question: "Como é definido o grau da deficiência?", answer: "Através de avaliação biopsicossocial realizada pelo INSS, que considera aspectos médicos e sociais da deficiência. A perícia analisa impedimentos de longo prazo de natureza física, mental, intelectual ou sensorial." },
      { question: "Posso usar tempo trabalhado antes de adquirir a deficiência?", answer: "Sim, o tempo anterior à deficiência pode ser usado, mas será convertido proporcionalmente. Por exemplo, tempo trabalhado como pessoa sem deficiência será convertido para o tempo correspondente de pessoa com deficiência." },
      { question: "A aposentadoria da pessoa com deficiência é diferente do BPC?", answer: "Sim, são benefícios diferentes. A aposentadoria requer contribuições ao INSS e não tem limite de renda. O BPC é benefício assistencial que não exige contribuição, mas tem critério de renda familiar e é destinado a quem nunca contribuiu ou não atingiu carência." }
    ]
  },

  "aposentadoria-rural": {
    heroDescription: "Reconhecimento da atividade rural para trabalhadores do campo, segurados especiais e boia-fria, com idade reduzida e possibilidade de contagem de tempo sem contribuição.",
    aboutTitle: "Aposentadoria Rural",
    aboutSubtitle: "Direitos do trabalhador do campo",
    aboutParagraphs: [
      "A aposentadoria rural possui <strong>regras diferenciadas</strong> que reconhecem a dificuldade do trabalho no campo. O trabalhador rural pode aposentar com idade reduzida e, em alguns casos, sem necessidade de contribuição ao INSS.",
      "Nosso trabalho é <strong>reunir as provas necessárias</strong> para comprovar a atividade rural, incluindo documentos antigos e testemunhos que demonstrem o exercício do trabalho no campo."
    ],
    highlights: [
      { icon: Sprout, title: "Idade Reduzida", description: "60 anos (H) ou 55 anos (M)" },
      { icon: FileCheck, title: "Sem Contribuição", description: "Segurado especial" },
      { icon: Clock, title: "Tempo Rural", description: "Soma com tempo urbano" },
      { icon: Award, title: "Provas Diversas", description: "Documentação variada" }
    ],
    featuresTitle: "Atuação Especializada",
    featuresSubtitle: "Experiência em direitos do trabalhador rural",
    features: [
      { icon: Sprout, title: "Aposentadoria por Idade Rural", description: "Benefício com idade reduzida de 60 anos (homens) e 55 anos (mulheres)." },
      { icon: FileCheck, title: "Segurado Especial", description: "Aposentadoria para quem trabalha em regime de economia familiar sem empregados." },
      { icon: FileText, title: "Comprovação de Atividade", description: "Reunião de documentos e testemunhos para comprovar trabalho rural." },
      { icon: Clock, title: "Tempo Rural + Urbano", description: "Soma de períodos rurais com urbanos para aposentadoria híbrida." },
      { icon: Brain, title: "Reconhecimento Retroativo", description: "Inclusão de tempo rural anterior a 1991 sem contribuição." },
      { icon: Shield, title: "Recursos e Ações", description: "Atuação em processos quando o INSS nega o reconhecimento da atividade rural." }
    ],
    faqs: [
      { question: "Preciso ter contribuído para o INSS para aposentar como rural?", answer: "Depende da categoria. O segurado especial (pequeno produtor, pescador artesanal, etc.) pode aposentar por idade sem contribuição formal, comprovando apenas a atividade rural por 15 anos. Já o empregado rural ou contribuinte individual precisa comprovar as contribuições." },
      { question: "Quais documentos comprovam atividade rural?", answer: "Certidão de casamento, título de eleitor, carteira de vacinação, contrato de arrendamento, notas de produtor rural, declaração do sindicato, entre outros. Documentos antigos da família também podem servir como início de prova material." },
      { question: "Posso somar tempo rural com tempo urbano?", answer: "Sim, através da aposentadoria híbrida é possível somar tempo rural e urbano. O tempo rural pode ser usado para completar carência ou tempo de contribuição na aposentadoria urbana." },
      { question: "Trabalhei na roça quando criança. Esse tempo conta?", answer: "Sim, o tempo de trabalho rural na infância pode ser reconhecido a partir dos 12 anos de idade (para períodos anteriores à Constituição de 1988) ou dos 14 anos (após 1988), desde que comprovado adequadamente." }
    ]
  },

  "aposentadoria-professor": {
    heroDescription: "Regras diferenciadas para profissionais da educação com tempo de magistério comprovado em educação infantil, ensino fundamental e médio.",
    aboutTitle: "Aposentadoria do Professor",
    aboutSubtitle: "Valorização do profissional da educação",
    aboutParagraphs: [
      "Os professores possuem <strong>regras especiais</strong> de aposentadoria que reconhecem a importância e os desafios da profissão. Essas regras se aplicam a quem exerce funções de magistério na educação básica.",
      "Com a Reforma da Previdência, houve mudanças importantes, mas existem <strong>regras de transição</strong> que podem beneficiar quem já estava no sistema antes de novembro de 2019."
    ],
    highlights: [
      { icon: GraduationCap, title: "25 Anos", description: "Tempo mínimo de magistério" },
      { icon: Clock, title: "Idade Reduzida", description: "57 anos (M) ou 60 anos (H)" },
      { icon: FileCheck, title: "Educação Básica", description: "Infantil, fundamental e médio" },
      { icon: Award, title: "Regras Especiais", description: "Transição favorável" }
    ],
    featuresTitle: "Serviços para Professores",
    featuresSubtitle: "Especialização em aposentadoria do magistério",
    features: [
      { icon: GraduationCap, title: "Análise de Tempo", description: "Verificação de todo período em funções de magistério para contagem especial." },
      { icon: FileCheck, title: "Comprovação de Magistério", description: "Orientação sobre documentos necessários para comprovar atividade docente." },
      { icon: Clock, title: "Regras de Transição", description: "Identificação da regra mais vantajosa para professores que já contribuíam antes de 2019." },
      { icon: Brain, title: "Planejamento", description: "Estudo do melhor momento para solicitar a aposentadoria." },
      { icon: FileText, title: "Requerimento", description: "Preparação completa do pedido junto ao INSS ou regime próprio." },
      { icon: Shield, title: "Recursos", description: "Atuação em casos de indeferimento ou não reconhecimento de tempo de magistério." }
    ],
    faqs: [
      { question: "Quais profissionais têm direito à aposentadoria do professor?", answer: "Professores que exercem funções de magistério na educação infantil, ensino fundamental e ensino médio, incluindo direção, coordenação e assessoramento pedagógico. Professores universitários não têm mais regras especiais no RGPS." },
      { question: "Quanto tempo preciso para aposentar como professor?", answer: "Após a Reforma, são necessários 25 anos de magistério mais idade mínima de 57 anos (mulheres) ou 60 anos (homens). Existem regras de transição por pontos e pedágio para quem já contribuía antes de 13/11/2019." },
      { question: "Diretor de escola tem direito à aposentadoria do professor?", answer: "Sim, funções de direção, coordenação e assessoramento pedagógico são consideradas funções de magistério e contam para aposentadoria especial do professor, desde que exercidas em estabelecimentos de educação básica." },
      { question: "Trabalhei em escola particular e pública. Como fica?", answer: "O tempo de magistério em escola particular conta para aposentadoria no INSS. Se você trabalhou em escola pública com regime próprio, é necessário verificar as regras de cada regime e a possibilidade de contagem recíproca ou aposentadoria proporcional em cada um." }
    ]
  },

  "revisao-beneficios": {
    heroDescription: "Correção de erros no cálculo do INSS. Identificamos falhas e garantimos a recuperação de valores pagos a menor nos últimos 5 anos.",
    aboutTitle: "Revisão de Benefícios",
    aboutSubtitle: "Seu benefício pode estar errado",
    aboutParagraphs: [
      "O INSS frequentemente comete <strong>erros no cálculo</strong> dos benefícios previdenciários. Períodos não computados, índices incorretos e regras mal aplicadas podem resultar em perdas significativas.",
      "Nossa análise minuciosa identifica todas as <strong>possibilidades de aumento</strong> do seu benefício, com recuperação de valores atrasados de até 5 anos."
    ],
    highlights: [
      { icon: FileCheck, title: "Análise Completa", description: "Carta de concessão e CNIS" },
      { icon: DollarSign, title: "Valores Atrasados", description: "Recuperação de até 5 anos" },
      { icon: Clock, title: "Prazo de 10 Anos", description: "Para pedir revisão" },
      { icon: Award, title: "Sem Risco", description: "Não reduz o benefício" }
    ],
    featuresTitle: "Tipos de Revisão",
    featuresSubtitle: "Identificamos todas as oportunidades de correção",
    features: [
      { icon: FileCheck, title: "Erro de Cálculo", description: "Correção de salários não considerados ou índices aplicados incorretamente." },
      { icon: Shield, title: "Atividade Especial", description: "Inclusão de períodos especiais não reconhecidos que aumentam o valor do benefício." },
      { icon: DollarSign, title: "Revisão do Teto", description: "Para benefícios limitados pelo teto nas datas de reajuste de 1998 e 2003." },
      { icon: Sprout, title: "Período Rural", description: "Inclusão de tempo rural não computado que melhora a média salarial." },
      { icon: Clock, title: "Buraco Negro", description: "Para benefícios concedidos entre outubro/88 e abril/91." },
      { icon: Brain, title: "Melhor Benefício", description: "Análise se havia direito a benefício mais vantajoso na data da concessão." }
    ],
    faqs: [
      { question: "Qual o prazo para pedir revisão?", answer: "O prazo decadencial é de 10 anos contados do primeiro dia do mês seguinte ao recebimento da primeira prestação. Após esse prazo, algumas revisões ainda podem ser possíveis se envolverem matéria de fato não analisada anteriormente." },
      { question: "Posso perder meu benefício se pedir revisão?", answer: "Não. A revisão só pode resultar em aumento do benefício. Por lei, não é possível reduzir o valor já concedido em razão de pedido de revisão do segurado (princípio da non reformatio in pejus)." },
      { question: "Como saber se minha aposentadoria pode ser revisada?", answer: "É necessário analisar sua carta de concessão, CNIS e memória de cálculo do benefício. Nosso escritório realiza essa análise técnica para identificar possíveis erros e oportunidades de aumento." },
      { question: "Quanto tempo demora um processo de revisão?", answer: "Na via administrativa, a resposta costuma vir em 30 a 90 dias. Na via judicial, o tempo médio é de 1 a 3 anos, podendo ser mais rápido com tutela de urgência em casos que demonstrem erro evidente." }
    ]
  },

  "auxilio-doenca": {
    heroDescription: "Defesa em processos de concessão, restabelecimento e prorrogação de benefícios por incapacidade temporária ou permanente.",
    aboutTitle: "Auxílio-Doença e Auxílio-Acidentário",
    aboutSubtitle: "Proteção quando você mais precisa",
    aboutParagraphs: [
      "Quando a doença ou o acidente afasta você do trabalho, é fundamental garantir sua <strong>fonte de renda</strong>. Os benefícios por incapacidade protegem o trabalhador durante o período em que não pode exercer suas atividades.",
      "Atuamos desde a <strong>concessão até recursos</strong> contra indeferimentos e altas médicas prematuras, sempre buscando a proteção dos seus direitos."
    ],
    highlights: [
      { icon: HeartPulse, title: "Incapacidade Temporária", description: "Auxílio-doença" },
      { icon: Shield, title: "Acidente de Trabalho", description: "Auxílio-acidentário" },
      { icon: FileCheck, title: "Restabelecimento", description: "Benefício cessado" },
      { icon: Award, title: "Adicional 25%", description: "Grande invalidez" }
    ],
    featuresTitle: "Serviços Oferecidos",
    featuresSubtitle: "Atuação completa em benefícios por incapacidade",
    features: [
      { icon: HeartPulse, title: "Auxílio por Incapacidade Temporária", description: "Antigo auxílio-doença, para quem está temporariamente incapaz de trabalhar." },
      { icon: Shield, title: "Aposentadoria por Invalidez", description: "Para casos de incapacidade total e permanente para qualquer atividade laboral." },
      { icon: FileCheck, title: "Auxílio-Acidente", description: "Benefício indenizatório para quem ficou com sequelas que reduzem a capacidade de trabalho." },
      { icon: DollarSign, title: "Adicional de 25%", description: "Acréscimo para aposentados por invalidez que necessitam de assistência permanente." },
      { icon: Clock, title: "Restabelecimento", description: "Atuação em casos de cessação indevida ou alta médica prematura." },
      { icon: Brain, title: "Recurso contra Indeferimento", description: "Recursos administrativos e ações judiciais contra negativas do INSS." }
    ],
    faqs: [
      { question: "Qual a diferença entre auxílio-doença e aposentadoria por invalidez?", answer: "O auxílio-doença (auxílio por incapacidade temporária) é para incapacidade temporária, com expectativa de recuperação. A aposentadoria por invalidez (aposentadoria por incapacidade permanente) é para casos em que não há possibilidade de retorno ao trabalho." },
      { question: "Preciso de carência para receber benefício por incapacidade?", answer: "Em regra, são necessários 12 meses de contribuição. Porém, em casos de acidente (de qualquer natureza) ou doenças graves listadas em lei (como câncer, AIDS, esclerose múltipla), não há exigência de carência." },
      { question: "O INSS negou meu pedido. O que fazer?", answer: "Você pode entrar com recurso administrativo na Junta de Recursos ou ingressar com ação judicial. Em casos urgentes, é possível pedir tutela de urgência para receber o benefício enquanto o processo tramita." },
      { question: "Posso trabalhar recebendo benefício por incapacidade?", answer: "Se você está recebendo auxílio-doença ou aposentadoria por invalidez, trabalhar pode caracterizar fraude. O auxílio-acidente é exceção e pode ser acumulado com o salário, pois tem natureza indenizatória." }
    ]
  },

  "bpc-loas": {
    heroDescription: "Benefício de Prestação Continuada para idosos e pessoas com deficiência em situação de vulnerabilidade. Um salário mínimo mensal sem precisar ter contribuído.",
    aboutTitle: "BPC/LOAS",
    aboutSubtitle: "Direito constitucional de proteção social",
    aboutParagraphs: [
      "O BPC/LOAS é um <strong>direito constitucional</strong> que garante um salário mínimo mensal para idosos (65 anos ou mais) e pessoas com deficiência que não possuem meios de se manter.",
      "Diferente da aposentadoria, o BPC <strong>não exige contribuição</strong> ao INSS. O requisito principal é comprovar a situação de vulnerabilidade econômica da família."
    ],
    highlights: [
      { icon: HandHeart, title: "1 Salário Mínimo", description: "Benefício mensal garantido" },
      { icon: Users, title: "Sem Contribuição", description: "Não exige INSS" },
      { icon: FileCheck, title: "Renda Familiar", description: "Análise de vulnerabilidade" },
      { icon: Award, title: "Idosos e PCD", description: "Proteção constitucional" }
    ],
    featuresTitle: "Como Ajudamos",
    featuresSubtitle: "Especialização em BPC/LOAS",
    features: [
      { icon: Users, title: "BPC para Idosos", description: "Benefício para pessoas com 65 anos ou mais em situação de vulnerabilidade." },
      { icon: HandHeart, title: "BPC para PCD", description: "Benefício para pessoas com deficiência de qualquer idade que comprovem vulnerabilidade." },
      { icon: FileCheck, title: "Análise de Renda", description: "Cálculo correto da renda per capita considerando exclusões legais e jurisprudência." },
      { icon: Brain, title: "Recurso contra Indeferimento", description: "Atuação quando o INSS nega o benefício, especialmente em casos de renda acima do limite." },
      { icon: Shield, title: "Manutenção do Benefício", description: "Defesa em processos de revisão e cortes indevidos do BPC já concedido." },
      { icon: FileText, title: "Documentação", description: "Orientação completa sobre documentos necessários para o requerimento." }
    ],
    faqs: [
      { question: "Qual a renda máxima para ter direito ao BPC?", answer: "A lei estabelece renda familiar per capita inferior a 1/4 do salário mínimo. Porém, a jurisprudência permite a concessão mesmo quando a renda é superior, desde que comprovada a situação de miserabilidade por outros meios." },
      { question: "Preciso ter contribuído para o INSS para receber BPC?", answer: "Não! O BPC é um benefício assistencial, não previdenciário. Não exige nenhuma contribuição prévia. É direito de quem nunca trabalhou ou contribuiu, desde que cumpra os requisitos de idade/deficiência e renda." },
      { question: "Quais benefícios são excluídos do cálculo da renda familiar?", answer: "São excluídos: benefícios assistenciais de outros membros, Bolsa Família/Auxílio Brasil, estágio supervisionado, renda de pessoa com deficiência que recebe BPC, entre outros definidos em lei." },
      { question: "O BPC pode ser acumulado com aposentadoria?", answer: "Não. O BPC não pode ser acumulado com nenhum outro benefício da seguridade social, exceto assistência médica e pensão especial indenizatória. Se a pessoa passar a receber aposentadoria, o BPC será cessado." }
    ]
  },

  "pensao-morte": {
    heroDescription: "Garantia do benefício para dependentes, incluindo cônjuges, companheiros, filhos, pais e irmãos, após o falecimento do segurado.",
    aboutTitle: "Pensão por Morte",
    aboutSubtitle: "Proteção para a família do segurado",
    aboutParagraphs: [
      "A pensão por morte é o benefício pago aos <strong>dependentes do segurado</strong> que falece. É fundamental para garantir a subsistência da família após a perda do provedor.",
      "Nosso trabalho é garantir que todos os dependentes recebam o benefício a que têm direito, com o <strong>valor correto</strong> e pelo tempo adequado."
    ],
    highlights: [
      { icon: Heart, title: "Dependentes", description: "Cônjuge, filhos, pais" },
      { icon: FileCheck, title: "Qualidade de Segurado", description: "Verificação de requisitos" },
      { icon: Clock, title: "Duração Variável", description: "Conforme idade e tempo" },
      { icon: Award, title: "União Estável", description: "Companheiros têm direito" }
    ],
    featuresTitle: "Atuação em Pensão por Morte",
    featuresSubtitle: "Proteção completa para os dependentes",
    features: [
      { icon: Heart, title: "Pensão para Cônjuge", description: "Benefício para esposo(a) ou companheiro(a) do segurado falecido." },
      { icon: Users, title: "Pensão para Filhos", description: "Direito dos filhos menores de 21 anos ou inválidos de qualquer idade." },
      { icon: HandHeart, title: "Pensão para Pais", description: "Quando não há dependentes de primeira classe e comprovada dependência econômica." },
      { icon: FileCheck, title: "Comprovação de União", description: "Auxílio na comprovação de casamento ou união estável." },
      { icon: Shield, title: "Qualidade de Segurado", description: "Análise se o falecido mantinha qualidade de segurado ou período de graça." },
      { icon: Brain, title: "Recurso e Ação Judicial", description: "Atuação em casos de indeferimento ou valor incorreto do benefício." }
    ],
    faqs: [
      { question: "Quem tem direito à pensão por morte?", answer: "Têm direito os dependentes do segurado: 1ª classe - cônjuge, companheiro(a) e filhos; 2ª classe - pais; 3ª classe - irmãos. A existência de dependentes de classe anterior exclui os de classe posterior." },
      { question: "Quanto tempo dura a pensão por morte para cônjuge?", answer: "Depende da idade e do tempo de casamento/união. Pode variar de 4 meses (casamento < 2 anos) até vitalícia (para quem tem 45 anos ou mais na data do óbito, se casado há mais de 2 anos)." },
      { question: "Preciso comprovar dependência econômica?", answer: "Para cônjuge, companheiro e filhos menores, a dependência é presumida. Para pais e irmãos, é necessário comprovar que dependiam economicamente do segurado falecido." },
      { question: "Companheiro(a) tem os mesmos direitos que cônjuge?", answer: "Sim, a união estável é equiparada ao casamento para fins de pensão por morte. É necessário comprovar a convivência pública, contínua e duradoura com objetivo de constituir família." }
    ]
  },

  "isencao-imposto-renda": {
    heroDescription: "Direito à isenção de IR para aposentados e pensionistas com doenças graves, com possibilidade de restituição dos valores pagos nos últimos 5 anos.",
    aboutTitle: "Isenção de Imposto de Renda",
    aboutSubtitle: "Direito do aposentado com doença grave",
    aboutParagraphs: [
      "Aposentados e pensionistas portadores de <strong>doenças graves</strong> têm direito à isenção do Imposto de Renda sobre seus proventos, conforme previsto na Lei 7.713/88.",
      "Além de deixar de pagar o imposto, é possível <strong>recuperar os valores</strong> descontados indevidamente nos últimos 5 anos, corrigidos monetariamente."
    ],
    highlights: [
      { icon: DollarSign, title: "Isenção Total", description: "Sem desconto de IR" },
      { icon: FileCheck, title: "Restituição", description: "Últimos 5 anos" },
      { icon: HeartPulse, title: "Doenças Graves", description: "Lista legal definida" },
      { icon: Award, title: "Laudo Médico", description: "Comprovação necessária" }
    ],
    featuresTitle: "Como Funciona",
    featuresSubtitle: "Processo completo para obter a isenção",
    features: [
      { icon: HeartPulse, title: "Análise do Direito", description: "Verificação se a doença se enquadra na lista de moléstias graves previstas em lei." },
      { icon: FileCheck, title: "Obtenção de Laudo", description: "Orientação sobre laudo médico necessário para comprovar a doença." },
      { icon: DollarSign, title: "Pedido Administrativo", description: "Requerimento de isenção junto à fonte pagadora ou Receita Federal." },
      { icon: Brain, title: "Restituição de Valores", description: "Recuperação do IR pago indevidamente nos últimos 5 anos." },
      { icon: Shield, title: "Ação Judicial", description: "Processo quando há negativa administrativa ou demora no reconhecimento." },
      { icon: FileText, title: "Declaração de IR", description: "Orientação sobre como declarar corretamente após obter a isenção." }
    ],
    faqs: [
      { question: "Quais doenças dão direito à isenção de IR?", answer: "A lei prevê: AIDS, alienação mental, cardiopatia grave, cegueira, contaminação por radiação, doença de Paget em estados avançados, doença de Parkinson, esclerose múltipla, espondiloartrose anquilosante, fibrose cística, hanseníase, hepatopatia grave, nefropatia grave, neoplasia maligna, paralisia irreversível e incapacitante, e tuberculose ativa." },
      { question: "Preciso de laudo médico oficial?", answer: "Sim, é necessário laudo pericial emitido por serviço médico oficial (público). Porém, laudos particulares podem ser usados em ação judicial. O laudo deve indicar a data de início da doença." },
      { question: "Posso recuperar o IR pago antes de descobrir o direito?", answer: "Sim, é possível pedir restituição dos valores pagos nos últimos 5 anos, contados da data do pedido. Os valores são corrigidos pela taxa Selic." },
      { question: "A isenção vale para qualquer tipo de renda?", answer: "Não, a isenção se aplica apenas aos proventos de aposentadoria, reforma ou pensão. Rendas de trabalho ativo, aluguéis e outras fontes continuam tributáveis normalmente." }
    ]
  },

  "planejamento-previdenciario": {
    heroDescription: "Estudo personalizado para definir a melhor estratégia de aposentadoria, evitando prejuízos futuros e garantindo o melhor benefício possível.",
    aboutTitle: "Planejamento Previdenciário",
    aboutSubtitle: "Sua aposentadoria planejada com estratégia",
    aboutParagraphs: [
      "O planejamento previdenciário é um <strong>estudo completo</strong> da sua situação junto ao INSS para identificar a melhor estratégia de aposentadoria.",
      "Um planejamento bem feito pode representar <strong>diferença de milhares de reais</strong> no seu benefício, além de evitar aposentadorias precipitadas ou perda de direitos."
    ],
    highlights: [
      { icon: FileText, title: "Análise do CNIS", description: "Histórico completo" },
      { icon: Brain, title: "Simulações", description: "Cenários comparados" },
      { icon: Clock, title: "Momento Ideal", description: "Quando aposentar" },
      { icon: Award, title: "Relatório Completo", description: "Estratégia personalizada" }
    ],
    featuresTitle: "O Que Analisamos",
    featuresSubtitle: "Estudo completo da sua situação previdenciária",
    features: [
      { icon: FileCheck, title: "Análise do CNIS", description: "Verificação detalhada de todo histórico contributivo, identificando pendências e vínculos não reconhecidos." },
      { icon: Brain, title: "Simulação de Cenários", description: "Comparativo entre diferentes datas e regras de aposentadoria para identificar o momento ideal." },
      { icon: Clock, title: "Regras de Transição", description: "Análise de todas as regras de transição da Reforma para identificar a mais vantajosa." },
      { icon: DollarSign, title: "Cálculo de Contribuições", description: "Orientação sobre valores e prazos de contribuição para maximizar o benefício." },
      { icon: Sprout, title: "Recuperação de Tempo", description: "Identificação de períodos que podem ser incorporados: militar, rural, especial, etc." },
      { icon: FileText, title: "Relatório Personalizado", description: "Documento completo com todas as opções, valores estimados e recomendação estratégica." }
    ],
    faqs: [
      { question: "Por que fazer planejamento previdenciário?", answer: "O planejamento evita perdas financeiras que podem chegar a centenas de milhares de reais. Muitos segurados se aposentam no momento errado ou deixam de reconhecer períodos importantes, recebendo valores muito inferiores ao que teriam direito." },
      { question: "Quanto tempo antes devo fazer o planejamento?", answer: "O ideal é começar de 5 a 10 anos antes da aposentadoria pretendida. Isso permite corrigir pendências no CNIS, fazer contribuições estratégicas e aguardar o melhor momento. Porém, mesmo quem está próximo pode se beneficiar da análise." },
      { question: "O que são as regras de transição?", answer: "São regras criadas pela Reforma da Previdência de 2019 para quem já estava contribuindo antes da mudança. Existem 5 regras diferentes e cada pessoa pode se enquadrar em uma diferente, com requisitos e cálculos distintos." },
      { question: "Vale a pena continuar contribuindo como autônomo?", answer: "Depende de análise individualizada. Em alguns casos, contribuições adicionais podem aumentar significativamente o benefício. Em outros, pode não fazer diferença ou até ser prejudicial. O planejamento responde essa pergunta para seu caso específico." }
    ]
  }
};

// Generate Service Pages
const generateServicePage = (serviceId) => {
  const service = SERVICES.find(s => s.id === serviceId);
  const content = pageContents[serviceId];
  
  if (!service || !content) return null;
  
  return () => <ServicePage service={service} content={content} />;
};

// Home Page
const Home = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 bg-slate-800/50 border border-amber-500/30 rounded-full text-sm font-medium text-amber-500">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span>Advocacia Especializada em Direito Previdenciário</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
              Seus Direitos <span className="text-amber-500">Previdenciários</span> Merecem Ser Defendidos com Excelência
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animate-delay-100">
              Aposentadorias, revisões de benefícios, auxílios, pensões e isenção de Imposto de Renda. Mais de 20 anos de experiência transformando direitos em conquistas reais para você e sua família.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-16 animate-fade-in-up animate-delay-200">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                onClick={trackWhatsAppClick}
                className="group inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-slate-900 font-bold rounded-lg hover:bg-amber-400 transition-all duration-300 shadow-lg hover:shadow-amber-500/50"
              >
                Solicite sua Análise
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </a>
              <a
                href={CONTACT.phoneLink}
                data-testid="hero-phone-btn"
                onClick={trackPhoneClick}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-bold rounded-lg border-2 border-white hover:bg-white/10 transition-all duration-300"
              >
                <Phone className="mr-2" size={20} />
                {CONTACT.phone}
              </a>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in-up animate-delay-300">
              {[
                { value: "20+", label: "Anos de Experiência" },
                { value: "3", label: "Especializações" },
                { value: "1000+", label: "Clientes Atendidos" },
                { value: "100%", label: "Atendimento Online" }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-4xl font-bold text-amber-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-500/20 text-amber-500 rounded-full text-sm font-bold uppercase tracking-wide">
              Áreas de Atuação
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Serviços em Direito Previdenciário</h2>
            <p className="text-lg text-slate-400">
              Assessoria jurídica completa para proteger seus direitos junto ao INSS e regimes próprios de previdência
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Foto */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/dr-lucas-mecatti.jpg"
                  alt="Dr. Lucas Mecatti - Advogado Especialista em Direito Previdenciário"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 to-transparent p-8">
                  <h3 className="text-2xl font-bold text-white mb-1">Dr. Lucas Mecatti</h3>
                  <p className="text-slate-300">Advogado | Especialista em Direito Previdenciário e Tributário</p>
                </div>
              </div>
            </div>
            {/* Conteúdo */}
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-bold uppercase tracking-wide">
                Sobre
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Sobre o Dr. Lucas Mecatti
              </h2>
              <h3 className="text-2xl font-semibold text-amber-600 mb-6">
                Mais de 20 anos dedicados à defesa dos seus direitos
              </h3>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Com uma trajetória sólida de mais de duas décadas no exercício da advocacia,
                  construí minha carreira com base em três pilares fundamentais: <strong>conhecimento técnico</strong>,
                  <strong> comprometimento com resultados</strong> e <strong>atendimento humanizado</strong>.
                </p>
                <p>
                  Ao longo desses anos, especializei-me profundamente em questões tributárias e previdenciárias,
                  conquistando <strong>três especializações</strong> que me capacitam a oferecer soluções jurídicas
                  precisas e eficazes para cada caso.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {[
                  { icon: Globe, title: "Atuação Nacional", description: "Atendimento em todo o território brasileiro, 100% online" },
                  { icon: Users, title: "Atendimento Personalizado", description: "Cada cliente recebe atenção individualizada e humanizada" },
                  { icon: Award, title: "Experiência Comprovada", description: "Centenas de processos conduzidos com sucesso" },
                  { icon: Shield, title: "Você em Boas Mãos", description: "Compromisso genuíno de lutar pelo que é seu por direito" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <item.icon size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
                <p className="text-lg italic text-slate-800">
                  "Minha missão é transformar direitos previstos em lei em benefícios concretos para você e sua família."
                </p>
                <p className="mt-2 text-sm font-bold text-slate-900">— Dr. Lucas Mecatti</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-500/20 text-amber-500 rounded-full text-sm font-bold uppercase tracking-wide">
              Por Que Nos Escolher
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Diferenciais Que Fazem a Diferença</h2>
            <p className="text-lg text-slate-400">Compromisso genuíno com seus direitos e sua tranquilidade</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Brain, title: "Conhecimento Especializado", description: "Três especializações e mais de duas décadas de estudo aprofundado em direito previdenciário e tributário." },
              { icon: Heart, title: "Atendimento Humanizado", description: "Entendemos que por trás de cada processo existe uma história única. Você é tratado com respeito e empatia." },
              { icon: Globe, title: "Atuação em Todo o Brasil", description: "Atendimento 100% online que permite auxiliar segurados em qualquer cidade do território nacional." },
              { icon: Zap, title: "Agilidade e Urgência", description: "Ações ajuizadas com pedido de liminar quando necessário, buscando resultados no menor tempo possível." },
              { icon: Eye, title: "Transparência Total", description: "Você acompanha cada etapa do processo com comunicação clara e relatórios constantes sobre o andamento." },
              { icon: MessageCircle, title: "Suporte Dedicado", description: "Canal direto para dúvidas e esclarecimentos. Resposta rápida em até 24 horas para todos os clientes." }
            ].map((item, i) => (
              <div key={i} className="bg-slate-900/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700 hover:border-amber-500 transition-all duration-300">
                <div className="w-14 h-14 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="text-amber-500" size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProcessSection />
      <FAQHome />
      <GoogleReviews />
      <CTASection />
    </PageLayout>
  );
};

// Main App with Routes
function App() {
  useAdTracking();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        {SERVICES.map(service => {
          const PageComponent = generateServicePage(service.id);
          return PageComponent ? (
            <Route key={service.id} path={service.path} element={<PageComponent />} />
          ) : null;
        })}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
